import {
  AccountFlagEnum,
  AccountPlanEnum,
  useAccountBillingInfoQuery,
} from '~/generated/graphql';

import { useCreditUsage } from './useCreditUsage';
import useCurrentAccount from './useCurrentAccount';

export const hubspotPlans = [
  AccountPlanEnum.HubspotFreeTier,
  AccountPlanEnum.HubspotPaidTier,
];

export const selfServePlans = [
  ...hubspotPlans,
  AccountPlanEnum.FreeTier,
  AccountPlanEnum.PaidTier,
];

export const useEntitlements = () => {
  const { account, currentUser, loading: loadingAccount } = useCurrentAccount();
  const { data: billingInfoData } = useAccountBillingInfoQuery();
  const { hasUsage, loading: loadingUsage } = useCreditUsage();

  const hasFlag = (flag: AccountFlagEnum) => !!account?.flags?.includes(flag);
  const hasPlans = (plans: AccountPlanEnum[]) =>
    !!account?.plans?.some((plan) => plans.includes(plan));

  const isLoading = loadingAccount || loadingUsage;
  const isOwner = currentUser?.role === 'owner';

  const isLegacyAccount = !hasPlans(hubspotPlans);

  /**
   * Add new entitlements in alphabetical order to keep the list in order
   * and make it easier to find the entitlements.
   *
   * Use entitlements in AppRoutes.tsx and NavBar.tsx to show/hide routes and
   * nav items respectively. Can also use entitlements in components to show/hide
   * content.
   *
   * @example canAccessAdvertising
   */
  const hasAccessAdvertising =
    isLegacyAccount && hasFlag(AccountFlagEnum.AllowPlatformAdvertisingMenu);

  const hasAccessBilling = isOwner && !!billingInfoData?.accountBillingInfo;

  const hasAccessCompanies = isLegacyAccount;
  const hasAccessEnrichment = isLegacyAccount;
  const hasAccessForms = isLegacyAccount;
  const hasAccessFormsHubspot = !isLegacyAccount;
  const hasAccessIntegrations = isLegacyAccount;
  const hasAccessPeople = isLegacyAccount;

  const hasAccessPlans = isOwner && hasPlans(selfServePlans);

  const hasAccessSettingsApi = isLegacyAccount;
  const hasAccessSettingsApiUsage =
    hasAccessSettingsApi && hasFlag(AccountFlagEnum.AllowApiUsageTab);
  const hasAccessSettingsConnectedServices = !isLegacyAccount;
  const hasAccessSettingsSecurity = isLegacyAccount;
  const hasAccessSettingsTeamCreation = isLegacyAccount;
  const hasAccessSettingsTeams = isLegacyAccount;
  const hasAccessSettingsTeamUsers = isLegacyAccount;

  const hasAccessUsage = hasUsage;

  return {
    hasAccessAdvertising,
    hasAccessBilling,
    hasAccessCompanies,
    hasAccessSettingsConnectedServices,
    hasAccessEnrichment,
    hasAccessForms,
    hasAccessFormsHubspot,
    hasAccessIntegrations,
    hasAccessPlans,
    hasAccessPeople,
    hasAccessSettingsApi,
    hasAccessSettingsApiUsage,
    hasAccessSettingsSecurity,
    hasAccessSettingsTeamCreation,
    hasAccessSettingsTeams,
    hasAccessSettingsTeamUsers,
    hasAccessUsage,
    isOwner,
    loading: isLoading,
  };
};
